import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import { t } from 'i18next';

import type { IndicatorColor } from '../RatioIndicator';
import type { GuidanceStat } from '../utils';

/** The majority side of a beat/miss split among reporters. */
export interface SurpriseStat {
  percentage: number;
  beat: boolean;
}

export interface KeyStat {
  labelKey: string;
  indicator?: IndicatorColor;
  ratio?: number;
  primaryText: string;
  primaryColor?: IndicatorColor;
  secondaryText?: string;
  secondarySeparator?: boolean;
}

export const toSurpriseKeyStat = (
  labelKey: string,
  stat?: SurpriseStat,
  decimalPlaces?: number
): KeyStat => {
  if (!stat) {
    return { labelKey, primaryText: '-' };
  }

  const color: IndicatorColor = stat.beat ? 'bullish' : 'bearish';

  return {
    labelKey,
    indicator: color,
    ratio: stat.percentage,
    primaryText: formatPercentageNumber({
      value: stat.percentage,
      decimalPlaces,
    }),
    primaryColor: color,
    secondaryText: t(
      stat.beat
        ? 'widget:themeOverview.tabs.earnings.keyStats.ofReportersBeat'
        : 'widget:themeOverview.tabs.earnings.keyStats.ofReportersMissed'
    ),
  };
};

export const toGuidanceKeyStat = (stat?: GuidanceStat): KeyStat => {
  const labelKey = 'widget:themeOverview.tabs.earnings.keyStats.guidance';

  if (!stat || (stat.raised === 0 && stat.lowered === 0)) {
    return { labelKey, primaryText: '-' };
  }

  const { raised, lowered, na } = stat;
  const raisedIsPrimary = raised >= lowered;
  const color: IndicatorColor = raisedIsPrimary ? 'bullish' : 'bearish';
  const primaryCount = raisedIsPrimary ? raised : lowered;
  const secondaryCount = raisedIsPrimary ? lowered : raised;

  return {
    labelKey,
    primaryText: `${primaryCount} ${t(
      raisedIsPrimary
        ? 'widget:themeOverview.tabs.earnings.keyStats.raised'
        : 'widget:themeOverview.tabs.earnings.keyStats.lowered'
    )}`,
    primaryColor: color,
    secondaryText: `${secondaryCount} ${t(
      raisedIsPrimary
        ? 'widget:themeOverview.tabs.earnings.keyStats.lowered'
        : 'widget:themeOverview.tabs.earnings.keyStats.raised'
    )} · ${na} ${t('widget:themeOverview.tabs.earnings.keyStats.na')}`,
    secondarySeparator: true,
  };
};

export const toGuidanceTableKeyStat = (stat?: GuidanceStat): KeyStat => {
  const labelKey = 'widget:themeOverview.tabs.earnings.keyStats.guidance';

  if (!stat || (stat.raised === 0 && stat.lowered === 0)) {
    return { labelKey, primaryText: '-' };
  }

  const { raised, lowered, na } = stat;

  return {
    labelKey,
    primaryText: `${raised} ${t(
      'widget:themeOverview.tabs.earnings.keyStats.raised'
    )}`,
    secondaryText: `· ${lowered} ${t(
      'widget:themeOverview.tabs.earnings.keyStats.lowered'
    )} · ${na} ${t('widget:themeOverview.tabs.earnings.keyStats.na')}`,
  };
};

export const toMedianKeyStat = (labelKey: string, value?: number): KeyStat => ({
  labelKey,
  primaryText: formatPercentageNumber({ value, addPlus: true }),
  primaryColor:
    value === undefined || value === 0
      ? undefined
      : value > 0
        ? 'bullish'
        : 'bearish',
});
